import React, { useEffect, useState } from 'react';
import ActionWall from 'shared/components/ActionWall';
import { Grid } from '@mui/material';
import { IMakeDecisionData } from '.';
import { colors } from 'styles/colors';
import CardWrapper from '../cardWrapper';
import { useCandidateApplication } from 'modules/hiring/hooks/useCandidateApplication';
import {
  CandidateApplicationsActivityDetailsSkeleton,
  CandidateApplicationsFormSkeleton,
} from '../candidateApplication/candidateApplicationsActivitySkeleton';
import useCandidateContactDetails from 'modules/hiring/hooks/useCandidateContactDetails';
import { CandidateApplicationDetailsBlock } from '../candidateApplication/candidateApplicationDetails';
import { mapCandidateApplicationFormData } from '../candidateApplication/candidateApplicationDetails.helper';
import {
  mapCandidateAppliedForData,
  mapCandidateContactDetailsData,
  mapDataForDecision,
} from './decisionModal.helper';
import MakeDecisionModalHeader from './makeDecisionModalHeader';
import CandidateApplicationActivity from '../candidateApplication/candidateApplicationActivity';
import { ICandidateProfileActivityHistory } from 'modules/hiring/models/candidateProfile.model';
import { IRecentHiringActivityCardData } from '../recentHiringActivity/recentHiringActivityCard';
import RecentHiringActivityShimmer from '../recentHiringActivity/recentHiringActivityShimmer';
import DecisionConfirmation from './decisionConfirmation';
import { THMDecisionModalActions } from 'modules/hiring/models/hiringDecisionOption';
import { axiosMakeHiringDecision } from 'modules/hiring/stateManagement/api';
import { customEventPublish } from 'modules/hiring/utilities';
import { Canceler } from 'axios';
import { useTokenClaims } from '@kf/ic2fx';
import { useTranslation } from 'react-i18next';

export interface ICandidateDetails {
  candidate_id: number;
  application_id: number;
}
export interface IHiringMakeDecision {
  org_id: number;
  requisition_id: number;
  activity_stage: string;
  current_Activity: string | null;
  application_status: string;
  comments: string;
  candidate_Details: ICandidateDetails[];
  current_activity_status: string;
  updated_by: string;
}

interface IMakeDecisionProps {
  id: string;
  data: IMakeDecisionData | null;
  open: boolean;
  handleClose: () => void;
}

const MakeDecisionMainModal = ({
  id,
  data,
  open,
  handleClose,
}: IMakeDecisionProps) => {
  const { t: translate } = useTranslation();
  const { data: candidateData, loading: applicationLoader } =
    useCandidateApplication({
      candidateId: data?.candidateId,
      requisitionID: data?.requisitionId?.toString(),
    });
  const candidateApplicationForm = mapCandidateApplicationFormData(
    candidateData?.[0],
    'modal'
  );
  const { contactDetailsData, loading: contactLoader } =
    useCandidateContactDetails(data?.candidateId);
  const candidateContactDetails =
    mapCandidateContactDetailsData(contactDetailsData);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [activeDecision, setActiveDecision] =
    useState<THMDecisionModalActions>();
  const candidateAppliedForDetails = mapCandidateAppliedForData(
    candidateData?.[0]
  );
  const [apiLoader, setApiLoader] = useState(false);
  const [note, setNote] = useState('');
  const { name: hiringManagerName } = useTokenClaims();
  let makeHiringDecisionCanceler: Canceler;

  const mapCandidateApplicationActivityDataToCardData = (
    activities: ICandidateProfileActivityHistory[]
  ): IRecentHiringActivityCardData[] => {
    return activities?.map((item) => ({
      name: data?.name ?? '',
      reqId: item?.jobRequistionId?.toString() ?? '',
      activityName: item?.activityName ?? '',
      date: item?.activityDate ?? '',
      activityStatus: item?.activityStatus ?? '',
    }));
  };

  const updateSelectedHiringAction = (
    hiringAction: THMDecisionModalActions
  ) => {
    setActiveDecision(hiringAction ? hiringAction : undefined);
  };

  const onSubmitSuccess = async (updatedNote?: string) => {
    /* istanbul ignore else */
    if (updatedNote && activeDecision) {
      setNote(updatedNote);
      const payload = mapDataForDecision(
        candidateData?.[0],
        updatedNote,
        activeDecision,
        hiringManagerName + ''
      );
      setApiLoader(true);
      const response = await axiosMakeHiringDecision(
        payload,
        (cancel: Canceler) => (makeHiringDecisionCanceler = cancel)
      );
      /* istanbul ignore else */
      if (response?.statusCode === 200) {
        setShowSuccessPopup(true);
      }
      setApiLoader(false);
    }
  };

  useEffect(() => {
    return () => {
      makeHiringDecisionCanceler?.();
    };
  }, []);

  return (
    <ActionWall
      dialogId={id}
      open={open}
      onCloseIcon={() => {
        handleClose();
        /* instanbul ignore else */
        if (showSuccessPopup) customEventPublish('makeDecision', {});
      }}
      closeIconButton
      successDisabled={false}
      titleClassName='kfic2-panelTitleBold'
      headerContent={
        <MakeDecisionModalHeader
          headerData={{
            activityName: candidateData?.[0]?.applicationStage,
            lastUpdatedDate: contactDetailsData?.lastUpdatedDate,
            ...data,
          }}
          selectedAction={updateSelectedHiringAction}
          activeDecision={activeDecision}
          loading={(contactLoader && applicationLoader) || apiLoader}
        >
          {activeDecision && !apiLoader && (
            <Grid item xs={12}>
              <DecisionConfirmation
                success={onSubmitSuccess}
                cancel={() => setActiveDecision(undefined)}
                activeDecision={activeDecision}
                data={data}
                showSuccessPopup={showSuccessPopup}
                defaultNote={note}
              />
            </Grid>
          )}
        </MakeDecisionModalHeader>
      }
      id={`${id}-modal`}
      tabIndex={0}
      aria-label={translate('makeDecision')}
      centerAlignment={true}
      dialogPaperSxProps={{
        p: 2.5,
        m: 0,
        maxWidth: '53.25rem',
      }}
      dialogContentSxProps={{
        mb: 0,
        pb: 0,
        maxHeight: 'calc(100% - 9.75rem)',
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          backgroundColor: colors?.tertiary.white,
          width: '1rem',
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          borderRadius: '0.6rem',
          backgroundColor: colors?.background.darkGray,
          border: `0.25rem ${colors?.tertiary.white} solid `,
        },
      }}
      dialogTitleSxProps={{
        px: 0,
        mb: 0,
        zIndex: 1,
      }}
      closeButtonStyleProps={{
        top: 0,
        padding: 0,
      }}
      closeIconStyleProps={{
        width: '1.25rem',
        height: '1.25rem',
      }}
      sxTitle={{
        mt: 0,
      }}
      saveBtnSxProps={{
        mt: 2.5,
      }}
    >
      <Grid container columnSpacing={'1.25rem'} rowSpacing={'1.25rem'}>
        <Grid item xs={12} sm={6}>
          {contactLoader || apiLoader ? (
            <CardWrapper>
              <CandidateApplicationsFormSkeleton
                defaultGrid={false}
                rowCount={1}
                count={2}
                sx={{ mt: 0, mb: '0.75rem' }}
              />
            </CardWrapper>
          ) : (
            <CardWrapper>
              <CandidateApplicationDetailsBlock
                title='contact'
                data={candidateContactDetails}
                gridLayout={[12]}
              />
            </CardWrapper>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {applicationLoader || apiLoader ? (
            <CardWrapper>
              <CandidateApplicationsFormSkeleton
                defaultGrid={false}
                rowCount={1}
                count={2}
                sx={{ mt: 0, mb: '0.75rem' }}
              />
            </CardWrapper>
          ) : (
            <CardWrapper>
              <CandidateApplicationDetailsBlock
                title='appliedFor'
                data={candidateAppliedForDetails}
                gridLayout={[12]}
              />
            </CardWrapper>
          )}
        </Grid>
        <Grid item xs={12}>
          {applicationLoader || apiLoader ? (
            <CardWrapper>
              <CandidateApplicationsFormSkeleton
                count={3}
                sx={{ mt: 0, mb: '0.75rem' }}
              />
              <Grid item xs={12} sm={7} sx={{ mt: '0.5rem' }}>
                <CandidateApplicationsActivityDetailsSkeleton
                  defaultGrid={false}
                  skeletonGridSxProps={{ mb: '0.5rem' }}
                  rowCount={1}
                  dataCount={1}
                />
              </Grid>
            </CardWrapper>
          ) : (
            <CardWrapper>
              <CandidateApplicationDetailsBlock
                title='applicationForm'
                data={candidateApplicationForm}
                titleSx={{ mb: '0.75rem' }}
                gridLayout={[7, 5]}
              />
            </CardWrapper>
          )}
        </Grid>
        <Grid item xs={12}>
          {applicationLoader || apiLoader ? (
            <CardWrapper>
              <RecentHiringActivityShimmer />
            </CardWrapper>
          ) : (
            <CandidateApplicationActivity
              enableDateTextSuffixForRHAGroupedList={false}
              data={mapCandidateApplicationActivityDataToCardData(
                candidateData?.[0]?.activityHistory
              )}
            />
          )}
        </Grid>
      </Grid>
    </ActionWall>
  );
};

export default MakeDecisionMainModal;
